<template>
  <div class="favourite-new-list-form" :class="{ desktop: $isDesktop }">
    <slot name="header">
      <translate tag="h3"> Create a new list </translate>
    </slot>
    <form novalidate @submit.prevent="save">
      <base-input
        id="wishlistName"
        ref="wishlistNameRef"
        :model-value="wishlistNameValue"
        :placeholder="i18n.t('e.g. Weekend breaks nearby')"
        :required="true"
        :disabled="isDisabled"
        type="text"
        maxlength="60"
        :validation-rules="wishlistNameValidation"
        :validation-errors="errors"
        name="name"
        class="list-name-input"
        @input="handleInput"
        @keyup.enter="save"
      >
        <template #label>
          <translate>List name</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate v-if="failedRules.includes('required')">
            Please name your list
          </translate>
          <span v-else>{{ failedRules }}</span>
        </template>
      </base-input>
      <div class="privacy">
        <base-select
          :select-options="selectOptions"
          :disabled="isDisabled"
          :required="true"
          name="status"
          @change="handleChange"
        >
          <template #label>
            <translate>Privacy</translate><favourite-list-privacy-popover />
          </template>
        </base-select>
      </div>
      <div class="button-container">
        <base-button
          type="submit"
          class="primary asblock"
          :disabled="isDisabled"
          @click="save"
        >
          <slot name="buttonContent">
            <translate> Create list </translate>
          </slot>
        </base-button>
      </div>
    </form>

    <slot name="footer" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { PrivacyType } from '../../types'
import { z } from 'zod'
import { useForm } from 'vee-validate'

const i18n = useI18n()

const props = withDefaults(
  defineProps<{
    wishlistName?: string
    status?: PrivacyType
    errors?: Record<string, string>
    isDisabled?: boolean
  }>(),
  {
    wishlistName: '',
    status: 'private',
    errors: undefined,
    isDisabled: false,
  },
)

const wishlistNameValue = ref('')
const wishlistPrivacyStatusValue = ref('')

onMounted(() => {
  wishlistNameValue.value = props.wishlistName
  wishlistPrivacyStatusValue.value = props.status
})

watch(
  () => props.wishlistName,
  (value) => {
    wishlistNameValue.value = value
  },
)

watch(
  () => props.status,
  (value) => {
    wishlistPrivacyStatusValue.value = value
  },
)

const wishlistNameValidation = z
  .string({ required_error: 'required' })
  .min(1, { message: 'required' })
  .max(60, { message: 'max' })

const emit = defineEmits(['input', 'change', 'saveToList'])

function handleChange(event) {
  wishlistPrivacyStatusValue.value = event.selectedValue
  emit('change', event.selectedValue)
}

function handleInput(event: string) {
  wishlistNameValue.value = event
  emit('input', event)
}

const selectOptions = computed(() => [
  {
    name: i18n.t('Public'),
    value: 'public',
    selected: props.status === 'public',
  },
  {
    name: i18n.t('Private'),
    value: 'private',
    selected: props.status === 'private',
  },
])

function save() {
  emit('saveToList', {
    wishlistName: wishlistNameValue.value,
    status: wishlistPrivacyStatusValue.value,
  })
}

const { setErrors } = useForm()
watch(
  () => props.errors,
  (newErrors) => {
    if (newErrors && Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
    }
  },
)
</script>

<style scoped>
.favourite-new-list-form {
  padding: 1em;
  width: 100%;
}

.privacy {
  margin: 1.5em 0;
}

.privacy :deep(.label-status) {
  color: transparent;
}

.privacy :deep(.label-status:after) {
  display: block;
  content: ' ';
}

.privacy :deep(.select) {
  margin: 0;
  min-width: 50%;
}

.privacy :deep(.pu-popover) {
  margin-left: 0.5em;
}

.list-name-input {
  margin-top: 2em;
  display: block;
}

/* desktop & tablet */
.desktop.favourite-new-list-form {
  padding: 1.5em;
}
</style>
